import config from '@app/config';
import Log from '@app/libs/logger';
import { CAPTCHA_TOKEN_HEADER } from '@supplierRegistration/domain/supplierRegistration';
import { RawAxiosRequestHeaders } from 'axios';

/**
 * Builds the reCAPTCHA request header for a supplier-registration API call.
 *
 * - Captcha flag OFF  → returns `{}` (the header is never sent).
 * - Flag ON + token   → returns `{ 'X-Recaptcha-Token': <token> }`.
 * - Flag ON + no token → returns `{}` and logs an exception. We still let the request
 *   go through (the flow is deliberately fail-open — the backend decides), but the miss
 *   is recorded so blocked / timed-out / dismissed captchas are visible in telemetry.
 *
 * `context` identifies the call site (e.g. `'store-validation'`, `'pre-registration'`)
 * so a tokenless request can be traced back to the exact flow that produced it.
 *
 * Spread the result into the request headers, e.g.:
 *   const headers = { ...baseHeaders, ...buildCaptchaHeaders(captchaToken, 'store-validation') };
 */
export function buildCaptchaHeaders(captchaToken: string | undefined, context: string): RawAxiosRequestHeaders {
  if (!config.featureFlags?.supplierRegistrationCaptcha) {
    return {};
  }

  if (captchaToken) {
    return { [CAPTCHA_TOKEN_HEADER]: captchaToken };
  }

  Log.exception(new Error(`[reCAPTCHA][${context}] request sent without token — captcha enabled but no token available`), {
    context,
    captchaEnabled: true,
    hasToken: false,
  });

  return {};
}
